import React, { useState, useEffect } from 'react';
import moment from 'moment';
import RecurrenceField from './RecurrenceField';
import { useFinanceData } from '../hooks/useFinanceData';

const EditIncomeForm = ({ initialValues, onSubmit, onCancel, onDelete, isOnboarding = false, useDirectUpdate = false }) => {
  // Use the useFinanceData hook to access editItem and addItemWithConflictCheck
  const { editItem, addItemWithConflictCheck, getItemPath } = useFinanceData();
  
  const [income, setIncome] = useState({
    title: '',
    amount: 0,
    date: moment(),
    recurrenceFrequency: null,
    recurrencePeriod: 'months',
    emoji: '💰',
    createdAt: moment().toISOString(),
  });

  // Available emoji options for the simple emoji picker
  const emojiOptions = ['💰', '💵', '💸', '🏦', '💼', '🏢', '🚗', '🏠', '🎓', '🎁'];
  
  useEffect(() => {
    if (initialValues) {
      console.log('Initial values received:', initialValues);
      setIncome({
        ...initialValues,
        title: initialValues.title,
        date: moment(initialValues.date),
        amount: initialValues.amount || 0,
      });
      console.log('Income state after setting initial values:', income);
    }
  }, []);

  const handleChange = (name, value) => {
    console.log('handleChange called with:', { name, value });
    console.log('Current income state before change:', income);
    
    setIncome(prevIncome => {
      const newIncome = { ...prevIncome, [name]: value };
      console.log('New income state after change:', newIncome);
      return newIncome;
    });
  };

  const validateIncomeData = (income) => {
    console.log('Validating income data:', income);
    const isValid = income.title && income.amount >= 0 && income.date;
    console.log('Validation result:', isValid);
    return isValid;
  };

  // Function to check if only the date changed - aligned with EditExpenseForm
  const isOnlyDateChanged = (currentIncome, initialIncome) => {
    if (!initialIncome) return false;

    // Convert dates to strings for comparison
    const currentDate = moment(currentIncome.date).format("YYYY-MM-DD");
    const initialDate = moment(initialIncome.date).format("YYYY-MM-DD");

    // Check if dates are different
    if (currentDate === initialDate) return false;

    // Check if all other relevant fields are the same
    const fieldsToCompare = [
      'title',
      'amount',
      'recurrenceFrequency',
      'recurrencePeriod',
      'emoji'
    ];

    return fieldsToCompare.every((field) => {
      if (Array.isArray(currentIncome[field])) {
        return (
          JSON.stringify(currentIncome[field]) ===
          JSON.stringify(initialIncome[field])
        )
      }
      return currentIncome[field] === initialIncome[field]
    });
  };

  // Updated handleSubmit function with date handling aligned with EditExpenseForm
  const handleSubmit = async () => {
    try {
      console.log('Starting handleSubmit');
      console.log('Current income state:', income);
      console.log('Initial values:', initialValues);
  
      if (!validateIncomeData(income)) {
        alert('Please fill in all required fields correctly');
        return;
      }
      
      // Check if the selected date is in the past
      if (moment(income.date).isBefore(moment(), 'day')) {
        alert("Invalid Date: Please select a date that is not in the past.");
        return;
      }
  
      console.log('INCOME UPDATE - Details:');
      console.log('Title:', income.title);
      console.log('Amount:', income.amount);
      console.log('Date:', income.date.format('YYYY-MM-DD'));
      console.log('Emoji:', income.emoji);
      console.log('Recurrence Frequency:', income.recurrenceFrequency);
      console.log('Recurrence Period:', income.recurrencePeriod);
      console.log('Is this occurrence recurring?', income.recurrencePeriod !== 'none' && income.recurrenceFrequency);
      
      // Prepare the income data
      const incomeData = {
        ...income,
        type: "income", 
        date: income.date.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
        amount: Number(income.amount)
      };

      // For existing incomes
      if (initialValues) {
        // Check if only the date changed
        const dateOnlyChanged = isOnlyDateChanged(income, initialValues);
        console.log('Is date-only change?', dateOnlyChanged);
        
        // Handle date changes for recurring incomes
        if (dateOnlyChanged && initialValues.recurrenceFrequency !== 'none') {
          console.log('Handling date change for recurring income');
          
          // Using confirm for options - same as in EditExpenseForm
          const updateChoice = window.confirm(
            "How would you like to update this date?\n\n" +
            "OK: Update this occurrence only\n" +
            "Cancel: Show more options"
          );
          
          if (updateChoice) {
            // User clicked OK - update single occurrence
            console.log('Updating single occurrence date');
            const occurrenceData = {
              ...incomeData,
              occurrenceDate: initialValues.date // Original date of the occurrence
            };

            if (useDirectUpdate) {
              const result = await editItem(occurrenceData, 'single');
              onSubmit(result, 'single');
            } else {
              onSubmit(occurrenceData, 'single');
            }
          } else {
            // User clicked Cancel - show more options
            const updateFuture = window.confirm(
              "Would you like to update all future occurrences to follow this new day pattern?\n\n" +
              "OK: Update all future occurrences to follow this new pattern\n" +
              "Cancel: Update all occurrences (past and future) to follow this new pattern"
            );
            
            if (updateFuture) {
              console.log('Updating future occurrences with new date pattern');
              // Include the date change and original occurrence date
              const futureData = {
                ...incomeData,
                occurrenceDate: initialValues.date, // Original date being modified
                datePatternChange: true // Flag to indicate date pattern change
              };
              
              if (useDirectUpdate) {
                const result = await editItem(futureData, 'future');
                onSubmit(result, 'future');
              } else {
                onSubmit(futureData, 'future');
              }
            } else {
              console.log('Updating all occurrences with new date pattern');
              // Include the date change flag
              const allData = {
                ...incomeData,
                datePatternChange: true // Flag to indicate date pattern change
              };
              
              if (useDirectUpdate) {
                const result = await editItem(allData, 'all');
                onSubmit(result, 'all');
              } else {
                onSubmit(allData, 'all');
              }
            }
          }
          return;
        }

        // Check if recurrence type is changing
        const isChangingToNonRecurring = 
          initialValues.recurrenceFrequency !== 'none' &&
          income.recurrencePeriod === 'none';
          
        const isChangingToRecurring = 
          (initialValues.recurrenceFrequency === 'none' || !initialValues.recurrenceFrequency) &&
          income.recurrencePeriod !== 'none';
          
        console.log('Is changing to non-recurring?', isChangingToNonRecurring);
        console.log('Is changing to recurring?', isChangingToRecurring);
        
        if (isChangingToNonRecurring || isChangingToRecurring) {
          console.log('Converting income recurrence type - updating all occurrences');
          
          if (useDirectUpdate) {
            const result = await editItem(incomeData, 'all');
            onSubmit(result, 'all');
          } else {
            onSubmit(incomeData, 'all');
          }
          return;
        }

        // Handle regular updates for recurring incomes
        if (income.recurrencePeriod !== 'none' && income.recurrenceFrequency !== 'none') {
          // Using alert for options as it's web-equivalent of Alert.alert in React Native
          const updateSingle = window.confirm(
            "How would you like to update this income?\n\n" +
            "OK: Update this occurrence only\n" +
            "Cancel: Show more options"
          );

          if (updateSingle) {
            // User clicked OK - update single occurrence
            console.log('Updating single occurrence');
            const occurrenceData = {
              ...incomeData,
              occurrenceDate: initialValues.date
            };

            if (useDirectUpdate) {
              const result = await editItem(occurrenceData, 'single');
              onSubmit(result, 'single');
            } else {
              onSubmit(occurrenceData, 'single');
            }
          } else {
            // User clicked Cancel - show more options
            const updateFuture = window.confirm(
              "Would you like to update all future occurrences?\n\n" +
              "OK: Update all future occurrences\n" +
              "Cancel: Update all occurrences (past and future)"
            );

            if (updateFuture) {
              console.log('Updating future occurrences');
              if (useDirectUpdate) {
                const result = await editItem(incomeData, 'future');
                onSubmit(result, 'future');
              } else {
                onSubmit(incomeData, 'future');
              }
            } else {
              console.log('Updating all occurrences');
              if (useDirectUpdate) {
                const result = await editItem(incomeData, 'all');
                onSubmit(result, 'all');
              } else {
                onSubmit(incomeData, 'all');
              }
            }
          }
        } else {
          // Non-recurring income update
          console.log('Updating non-recurring income');
          if (useDirectUpdate) {
            const result = await editItem(incomeData, 'all');
            onSubmit(result, 'all');
          } else {
            onSubmit(incomeData, 'all');
          }
        }
      } else {
        // New income
        console.log('Creating new income');
        console.log('New income data:', incomeData);
        if (useDirectUpdate) {
          const result = await addItemWithConflictCheck(incomeData);
          onSubmit(result);
        } else {
          onSubmit(incomeData);
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      alert('Please check all fields and try again');
    }
  };

  const handleDelete = () => {
    console.log('Starting handleDelete in EditIncomeForm');
    console.log('Current income state:', {
      recurrencePeriod: income.recurrencePeriod,
      recurrenceFrequency: income.recurrenceFrequency,
      date: income.date
    });

    // For non-recurring incomes, show simple confirmation
    if (income.recurrencePeriod === 'none' || income.recurrenceFrequency === 'none') {
      console.log('Income is non-recurring, showing simple delete confirmation');
      const confirmDelete = window.confirm("Are you sure you want to delete this income?");
      
      if (confirmDelete) {
        console.log('Calling onDelete with simple delete:', {
          incomeId: income.id,
          deleteType: undefined,
          date: undefined
        });
        onDelete(income);
      }
      return;
    }

    console.log('Income is recurring, showing delete options');
    // For recurring incomes, update to use confirm dialog instead of prompt for consistency
    const deleteType = window.confirm(
      "How would you like to delete this income?\n\n" +
      "OK: Delete this occurrence only\n" +
      "Cancel: Show more options"
    );

    if (deleteType) {
      console.log('Selected: Delete single occurrence', {
        incomeId: income.id,
        deleteType: 'single',
        date: income.date,
        recurrencePeriod: income.recurrencePeriod
      });
      onDelete(income, 'single', income.date);
    } else {
      // User clicked Cancel - show more options
      const moreOptions = window.confirm(
        "Would you like to delete all future occurrences?\n\n" +
        "OK: Delete this & all future occurrences\n" +
        "Cancel: Delete all occurrences (past and future)"
      );

      if (moreOptions) {
        console.log('Selected: Delete future occurrences', {
          incomeId: income.id,
          deleteType: 'future',
          date: income.date,
          recurrencePeriod: income.recurrencePeriod
        });
        onDelete(income, 'future', income.date);
      } else {
        console.log('Selected: Delete all occurrences', {
          incomeId: income.id,
          deleteType: 'all',
          recurrencePeriod: income.recurrencePeriod
        });
        onDelete(income, 'all');
      }
    }
  };

  // Handle date change
  const handleDateChange = (e) => {
    const newDate = moment(e.target.value);
    if (newDate.isValid()) {
      handleChange('date', newDate);
    }
  };

  return (
    <div className="edit-income-form">
      <h2 className="form-title">
        {isOnboarding || !initialValues ? 'ADD INCOME' : 'UPDATE INCOME'}
      </h2>

      <div className="form-group">
        <label className="form-label">Title</label>
        <input
          type="text"
          className="form-input"
          placeholder="e.g., Salary, Freelance"
          value={income.title}
          onChange={(e) => handleChange('title', e.target.value)}
        />
      </div>

      {/* Simple emoji picker replaces EmojiColorPicker */}
      <div className="form-group">
        <label className="form-label">Icon</label>
        <div className="emoji-picker">
          {emojiOptions.map(emoji => (
            <button
              key={emoji}
              type="button"
              className={`emoji-button ${income.emoji === emoji ? 'selected' : ''}`}
              onClick={() => handleChange('emoji', emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Amount</label>
        <div className="currency-input-container">
          <span className="currency-symbol">$</span>
          <input
            type="number"
            className="form-input"
            value={income.amount}
            onChange={(e) => handleChange('amount', e.target.value)}
            min="0"
            step="1"
            onKeyDown={(e) => {
              // Allow only digits, backspace, arrow keys, tab, delete
              if (!/^\d$/.test(e.key) && 
                  !['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(e.key)) {
                if (e.key === '.' || e.key === ',') {
                  // Prevent decimal input for whole numbers
                  e.preventDefault();
                } else if (!e.ctrlKey && !e.metaKey) {
                  // Allow keyboard shortcuts like Ctrl+C, Ctrl+V
                  e.preventDefault();
                }
              }
            }}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Date</label>
        <input
          type="date"
          className="form-input"
          value={income.date.format('YYYY-MM-DD')}
          onChange={handleDateChange}
        />
      </div>

      <RecurrenceField
        frequency={income.recurrenceFrequency}
        period={income.recurrencePeriod}
        onFrequencyChange={(value) => {
          console.log('RecurrenceField frequency change:', value);
          handleChange('recurrenceFrequency', value === 'none' ? 'none' : parseInt(value, 10) || 1);
        }}
        onPeriodChange={(value) => {
          console.log('RecurrenceField period change:', value);
          handleChange('recurrencePeriod', value);
        }}
      />

      <div className="form-actions">
        <button className="save-button" onClick={handleSubmit}>
          {isOnboarding || !initialValues ? 'Save Income' : 'Update Income'}
        </button>
        <button className="cancel-button" onClick={onCancel}>
          Cancel
        </button>
      </div>

      {!isOnboarding && initialValues && (
        <button onClick={handleDelete} className="delete-button">
          Delete
        </button>
      )}

      <style jsx>{`
        .edit-income-form {
          background-color: white;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          max-width: 500px;
          width: 100%;
        }

        .form-title {
          text-align: center;
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 1.1px;
          margin-bottom: 1.5rem;
        }

        .form-group {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .form-label {
          flex: 1;
          font-weight: 500;
          margin-right: 1rem;
        }

        .form-input {
          flex: 2;
          padding: 0.5rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 1rem;
        }

        .currency-input-container {
          display: flex;
          align-items: center;
          flex: 2;
        }

        .currency-symbol {
          margin-right: 0.5rem;
          font-size: 1rem;
        }

        .form-actions {
          display: flex;
          justify-content: space-between;
          margin-top: 1.5rem;
        }

        .save-button, .cancel-button {
          flex: 1;
          padding: 0.75rem;
          border-radius: 8px;
          border: none;
          font-weight: 500;
          cursor: pointer;
        }

        .save-button {
          background-color: #333;
          color: white;
          margin-right: 0.5rem;
        }

        .cancel-button {
          background-color: #e5e7eb;
          color: #333;
          margin-left: 0.5rem;
        }

        .delete-button {
          display: block;
          margin: 1.5rem auto 0;
          background: none;
          border: none;
          color: #dc2626;
          font-size: 1rem;
          cursor: pointer;
        }

        /* Styles for emoji picker */
        .emoji-picker {
          display: flex;
          flex-wrap: wrap;
          flex: 2;
          gap: 0.5rem;
        }

        .emoji-button {
          width: 2rem;
          height: 2rem;
          font-size: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .emoji-button:hover {
          background-color: #f3f4f6;
        }

        .emoji-button.selected {
          background-color: #f3f4f6;
          border-color: #333;
        }
      `}</style>
    </div>
  );
};

export default EditIncomeForm;